import { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { ButtonProps } from 'components/atoms/button';
import { Button } from 'components/facade/button';
import { ClassNameOnlyProps } from 'components/ozone-component';

export interface Action {
  title: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  type?: ButtonProps['type'];
  dataCy?: string;
}

interface Props extends ClassNameOnlyProps {
  primary: Action;
  secondary?: Action;
  other?: Action[];
  reverse?: boolean;
}

const ActionButton: FC<
  React.PropsWithChildren<Action & ClassNameOnlyProps>
> = ({ className, title, onClick, disabled, type, danger, dataCy }) => (
  <Button
    className={className}
    size="small"
    type={type ?? 'text'}
    onClick={onClick}
    disabled={disabled}
    danger={danger}
    data-cy={dataCy ?? `action-panel.footer.btn.${title}`}
  >
    {title}
  </Button>
);

const Wrapper = styled.div<{ $reverse: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: ${({ $reverse }) => ($reverse ? 'row-reverse' : 'row')};

  ${Button} {
    padding: 0 calc(var(--spacing-05) - 1px);
    ${({ $reverse }) =>
      !$reverse &&
      css`
        margin-right: var(--spacing-02);
      `};
    ${({ $reverse }) =>
      $reverse &&
      css`
        margin-left: var(--spacing-02);
      `};

    &:last-child {
      margin: 0;
    }
  }
`;

const ActionFooterComponent: FC<React.PropsWithChildren<Props>> = ({
  className,
  primary,
  secondary,
  other = [],
  reverse,
}) => (
  <Wrapper
    className={className}
    $reverse={!!reverse}
    data-cy="action-panel.footer.wrapper"
  >
    <ActionButton dataCy="action-panel.footer.primary-btn" {...primary} />
    {secondary == null ? null : (
      <ActionButton dataCy="action-panel.footer.secondary-btn" {...secondary} />
    )}
    {other.map((action, idx) => (
      <ActionButton key={idx} {...action} />
    ))}
  </Wrapper>
);

export const ActionFooter = styled(ActionFooterComponent)``;
